// web-search-tier1.ts — the sources a BROWSER is allowed to read directly.
//
// These send `Access-Control-Allow-Origin`, so the customer's browser talks straight to them:
// no reader, no proxy, NOBODY IN THE MIDDLE learns what the customer is researching. That is why
// Tier 1 is preferred over the open-web reader, not just tolerated alongside it.
//
// Verified live 2026-08-14 with a real cross-origin GET (headers AND body checked):
//   Wikipedia   ACAO *   search + FULL article plaintext (31,696 chars for "Heat pump")
//   OpenAlex    ACAO *   250M works — title, year, DOI, citation count
//   Crossref, Europe PMC, Stack Exchange, Hacker News, DuckDuckGo Instant Answer — all ACAO ok
//   arXiv       ✗ no ACAO   ·   SearXNG public instances ✗   ·   Mojeek / s.jina.ai ✗ (key)
// Full table + method: champion-lifecycle/WEB-SEARCH-FEATURE.md
//
// Coverage is deliberately encyclopaedic + academic + technical. It cannot reach news or blogs —
// that is what the open-web reader is for. For a research app this is the HIGHER-provenance half:
// it is the same source base Elicit and Consensus are built on.

export interface WebResult {
  title: string;
  url: string;
  snippet: string;
}

/** Hosts whose article text can be pulled directly via the MediaWiki API. */
export const WIKI_HOSTS = /(^|\.)wikipedia\.org$/i;

const timeout = (ms: number) => {
  const c = new AbortController();
  setTimeout(() => c.abort(), ms);
  return c.signal;
};

async function getJson<T>(url: string, signal?: AbortSignal): Promise<T | null> {
  try {
    const r = await fetch(url, { signal: signal ?? timeout(8000) });
    if (!r.ok) return null;
    return (await r.json()) as T;
  } catch {
    return null;
  }
}

/** Wikipedia full-text search. `origin=*` is what makes the API answer a browser. */
async function wikipedia(q: string, limit: number, signal?: AbortSignal): Promise<WebResult[]> {
  const j = await getJson<{ query?: { search?: Array<{ title: string; snippet?: string }> } }>(
    `https://en.wikipedia.org/w/api.php?action=query&list=search&format=json&origin=*&srlimit=${limit}&srsearch=${encodeURIComponent(q)}`,
    signal,
  );
  return (j?.query?.search ?? []).map((s) => ({
    title: s.title,
    url: `https://en.wikipedia.org/wiki/${encodeURIComponent(s.title.replace(/ /g, "_"))}`,
    // The API marks matches with HTML; the model wants prose.
    snippet: (s.snippet ?? "").replace(/<[^>]+>/g, "").trim().slice(0, 600),
  }));
}

/**
 * OpenAlex reads `?` and `*` in `search=` as WILDCARDS and answers 400 for the whole query
 * ("Wildcards (* or ?) require exact (no-stem) search"). Customers ask questions, so the most
 * common input of all — a sentence ending in "?" — returned no papers at all, and the answer fell
 * back to unsourced reasoning without ever saying the lookup had failed. Verified live 2026-09-01:
 * the identical query is 400 with the "?" and 200 without it.
 */
const openAlexQuery = (q: string) => q.replace(/[?*]/g, " ").replace(/\s+/g, " ").trim();

/** OpenAlex — real papers. Cited-by count is a genuine quality signal a general search cannot give. */
async function openAlex(q: string, limit: number, signal?: AbortSignal): Promise<WebResult[]> {
  const oq = openAlexQuery(q);
  if (!oq) return [];   // the query was nothing but wildcards
  const j = await getJson<{
    results?: Array<{ title?: string; doi?: string; publication_year?: number; cited_by_count?: number; id?: string }>;
  }>(`https://api.openalex.org/works?per-page=${limit}&search=${encodeURIComponent(oq)}`, signal);
  return (j?.results ?? [])
    .filter((w) => w.title && (w.doi || w.id))
    .map((w) => ({
      title: w.title!,
      url: w.doi || w.id!,
      snippet: `Paper${w.publication_year ? `, ${w.publication_year}` : ""}${
        typeof w.cited_by_count === "number" ? `, cited by ${w.cited_by_count}` : ""
      }.`,
    }));
}

/**
 * Ask the sources that permit a direct browser read.
 *
 * Runs them in parallel and INTERLEAVES the results rather than concatenating: one encyclopaedic
 * source and one academic source beat five of either, and a failing source must never starve the
 * other (each already resolves to [] on any error).
 */
export async function tierOneSearch(q: string, limit = 5, signal?: AbortSignal): Promise<WebResult[]> {
  const [wiki, papers] = await Promise.all([
    wikipedia(q, Math.ceil(limit / 2), signal).catch(() => []),
    openAlex(q, Math.ceil(limit / 2), signal).catch(() => []),
  ]);
  const out: WebResult[] = [];
  for (let i = 0; i < Math.max(wiki.length, papers.length) && out.length < limit; i++) {
    if (wiki[i]) out.push(wiki[i]);
    if (papers[i] && out.length < limit) out.push(papers[i]);
  }
  return out;
}
